'use client';

import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { VariantProps } from 'tailwind-variants';

import { pushClickMaterialGtm } from '@/fo/api/gtm';
import WithDoctorIcon from '@/fo/icons/base/blog/with-doctor.svg';
import PlayIcon from '@/fo/icons/base/icons/play.svg';
import { UiKitCarouselBase } from '@/fo/ui/kit/carousel/base';
import { UiKitLabelBase } from '@/fo/ui/kit/label/base';
import { UiModArtDirection } from '@/fo/ui/mod/art/direction';
import { useMediaQuery } from '@/fo/ui/mod/media/query';

import { ArticleBanner } from '../types';

import { styles } from './UiArticleBannerBlog.styles';

const AUTOPLAY_DELAY = 10000;

type Variants = VariantProps<typeof styles>;
export interface UiArticleBannerBlogProps {
  banners: ArticleBanner[];
  variant?: Variants['variant'];
  isVideo?: boolean;
}

export function UiArticleBannerBlog({
  banners,
  variant,
  isVideo
}: UiArticleBannerBlogProps) {
  const slots = styles({ variant });

  const t = useTranslations();

  const isMobile = useMediaQuery('md', 'max');

  return (
    <div className={slots.base()}>
      <UiKitCarouselBase
        options={{ active: banners.length > 1, watchDrag: isMobile }}
        className={slots.carousel()}
        showArrows
        arrowType="arrow"
        arrowPrevClass={slots.button()}
        arrowNextClass={slots.button()}
        autoplay={AUTOPLAY_DELAY}
        fade
        showDots={isMobile}
        dotsClass={slots.dots()}
        activeDotClass={slots.activeDot()}
        renderButtons={(buttons) => (
          <div className={slots.buttonsContainer()}>
            <div className={slots.container()}>
              <div className={slots.content()}>
                <div className={slots.buttons()}>{buttons}</div>
              </div>
            </div>
          </div>
        )}
      >
        {banners.map((banner) => (
          <div
            key={banner.id}
            className={slots.slide()}
          >
            <div className={slots.imageContainer()}>
              <div className={slots.mask()} />
              <UiModArtDirection
                className={slots.picture()}
                imageClassName={slots.image()}
                alt={banner.imageAlt}
                images={[
                  { src: banner.imageUrl, media: '(min-width: 768px)' },
                  { src: banner.mobileImageUrl }
                ]}
              />
            </div>

            <div className={slots.container()}>
              <div className={slots.content()}>
                <Link
                  href={banner.articleUrl}
                  className={slots.link()}
                  onClick={() => {
                    if (!banner.materialType) return;

                    pushClickMaterialGtm({
                      eventCategory: banner.tags?.[0]?.title || '',
                      moduleType: banner.materialType,
                      moduleTitle: banner.title
                    });
                  }}
                />
                {banner.tags && (
                  <div className={slots.tagsContainer()}>
                    {banner.tags.map((tag) => {
                      const hrefProps = tag.href
                        ? { href: tag.href, as: 'link' as const }
                        : {};

                      return (
                        <UiKitLabelBase
                          key={tag.id}
                          className={slots.tag()}
                          color="yellow"
                          customColor={tag.color}
                          textColor={
                            tag.color ? 'var(--color-white)' : undefined
                          }
                          {...hrefProps}
                          data-with-hover={!!tag.href}
                          uppercase
                        >
                          {tag.title}
                        </UiKitLabelBase>
                      );
                    })}
                  </div>
                )}
                {banner.withDoctor && (
                  <div className={slots.withDoctor()}>
                    <WithDoctorIcon />
                  </div>
                )}
                <h2 className={slots.title()}>{banner.title}</h2>
                {isVideo && (
                  <div className={slots.videoPlay()}>
                    <div className={slots.videoIconWrap()}>
                      <PlayIcon className={slots.videoIcon()} />
                    </div>
                    <div className={slots.videoText()}>{t('watch')}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </UiKitCarouselBase>
    </div>
  );
}
